// Pure types + projection helpers for the admin dashboard. No secrets, no Node
// APIs — the DB aggregation lives in netlify/functions/admin-stats.ts, which
// feeds raw rows through projectAdminPlayers() so the merge/sort logic stays
// unit-testable. Browser-safe (the Admin page imports the view types).

/** Top-of-dashboard usage summary. */
export interface AdminSummary {
  /** Persistent player profiles minted (anonymous-first). */
  totalPlayers: number;
  /** Profiles secured with a recovery passphrase (claimed for cross-device). */
  claimedPlayers: number;
  /** Recorded race results (every finished solo/daily run posted to a board). */
  totalGames: number;
  /** Multiplayer race rooms created (quick/private — solo is DB-free). */
  totalRaces: number;
  /** Badges earned across all players. */
  totalBadges: number;
  /** Profiles seen within the last 7 days. */
  activePlayers7d: number;
  /** Mean finished games per sit-down session over the trailing window
   *  (USAGE_WINDOW_DAYS). 0 when there are no sessions. Aggregate, non-personal. */
  avgGamesPerSession: number;
  /** Sessions counted in that window — the metric's denominator. Lets the UI
   *  flag a small sample (USAGE_MIN_SESSIONS) instead of showing noise. */
  windowSessions: number;
  /** Mean accuracy (0..1) across all recorded results. */
  avgAccuracy: number;
}

/** Trailing window (days) for the games-per-session engagement metric. 30 keeps
 *  the ratio steady at launch traffic; drop to 7 once volume justifies a more
 *  responsive read. Used by admin-stats.ts (query) and the Admin tile (label). */
export const USAGE_WINDOW_DAYS = 30;

/** Below this many sessions in the window the ratio is too noisy to trust, so
 *  the dashboard shows "—" / "low data" instead of a misleading number. */
export const USAGE_MIN_SESSIONS = 20;

/** Mean finished games per sit-down session. Guards divide-by-zero → 0. */
export function gamesPerSession(games: number, sessions: number): number {
  return sessions > 0 ? games / sessions : 0;
}

// --- Usage counters: kinds + derived funnel metrics -------------------------
//
// Everything here is driven by `usage_counters`, which is one row per (UTC day,
// kind) with NO identifier of any sort — see db/schema.ts. Adding a metric means
// adding a `kind` string, not a column, so none of this needs a migration.
//
// Because the rows carry no identity, every metric below is a **ratio of
// totals**, not a per-user funnel: "0.4 claims per finished race" across all
// players, never "40% of players converted". Read them that way.

/** The four things a player can race. */
export const RACE_MODES = ["solo", "daily", "quick", "room"] as const;
export type RaceMode = (typeof RACE_MODES)[number];

/** Every counter kind the client may report. `track-usage.ts` validates against
 *  this list, so the table can't be filled with junk kinds by anyone poking the
 *  public endpoint.
 *
 *  - `session` / `game` are the ORIGINAL two kinds. `game` deliberately still
 *    counts only solo+daily finishes (where it has always fired), so the
 *    `avgGamesPerSession` series stays comparable across the change. Use the
 *    `finish:*` kinds for a complete, all-mode picture.
 *  - `pick:*` — a mode button was pressed on Home (top of the funnel).
 *  - `start:*` — the server actually handed back a race.
 *  - `finish:*` — the race reached its finish screen.
 *  - action kinds — post-race behaviour worth a conversion rate. */
export const USAGE_KINDS = [
  "session",
  "game",
  "pick:solo",
  "pick:daily",
  "pick:quick",
  "pick:room",
  "pick:leaderboard",
  "start:solo",
  "start:daily",
  "start:quick",
  "start:room",
  "finish:solo",
  "finish:daily",
  "finish:quick",
  "finish:room",
  "share:used",
  "score:posted",
  "claim:done",
] as const;
export type UsageKind = (typeof USAGE_KINDS)[number];

/** Window totals, kind → count. Sparse: a kind nobody hit is simply absent. */
export type UsageTotals = Partial<Record<UsageKind, number>>;

/** Below this many events in the denominator a ratio is noise, so the dashboard
 *  shows "—" instead of a confident-looking number built from four data points. */
export const USAGE_MIN_DENOM = 20;

/** Plain ratio; 0 when the denominator is 0. */
export function ratio(numerator: number, denominator: number): number {
  return denominator > 0 ? numerator / denominator : 0;
}

/** Display ratio: null when the sample is too thin to mean anything, which the
 *  UI renders as "—". Keeps the "is this real?" judgement in one tested place. */
export function rate(
  numerator: number,
  denominator: number,
  min: number = USAGE_MIN_DENOM,
): number | null {
  if (denominator < min) return null;
  return ratio(numerator, denominator);
}

/** One mode's slice of the funnel. */
export interface UsageFunnelRow {
  mode: RaceMode;
  picks: number;
  starts: number;
  finishes: number;
  /** starts/picks — catches players who open a mode then back out (the solo
   *  options panel is the main place this happens). Null when too thin. */
  startRate: number | null;
  /** finishes/starts — the headline "do they play it through?" number. */
  completionRate: number | null;
}

export interface UsageMetrics {
  window: { picks: number; starts: number; finishes: number };
  /** Per-mode funnel, busiest mode first. */
  funnel: UsageFunnelRow[];
  /** finishes/starts across all modes. */
  completionRate: number | null;
  /** Shares per finished race — the organic-growth signal. */
  shareRate: number | null;
  /** Scores posted per finished solo/daily race (only those can post). */
  postRate: number | null;
  /** Profiles claimed per finished race — the retention conversion. */
  claimRate: number | null;
  /** Leaderboard opens from Home. */
  leaderboardViews: number;
}

const at = (t: UsageTotals, k: UsageKind): number => t[k] ?? 0;

/** Roll the raw kind→count map into the funnel + conversion rates the dashboard
 *  shows. Pure, so it is unit-tested rather than eyeballed in prod. */
export function usageMetrics(totals: UsageTotals): UsageMetrics {
  const funnel: UsageFunnelRow[] = RACE_MODES.map((mode) => {
    const picks = at(totals, `pick:${mode}` as UsageKind);
    const starts = at(totals, `start:${mode}` as UsageKind);
    const finishes = at(totals, `finish:${mode}` as UsageKind);
    return {
      mode,
      picks,
      starts,
      finishes,
      startRate: rate(starts, picks),
      completionRate: rate(finishes, starts),
    };
  }).sort((a, b) => b.starts - a.starts || b.picks - a.picks);

  const sum = (pick: (r: UsageFunnelRow) => number): number =>
    funnel.reduce((n, r) => n + pick(r), 0);
  const picks = sum((r) => r.picks);
  const starts = sum((r) => r.starts);
  const finishes = sum((r) => r.finishes);

  // Only solo/daily reach a score-post button, so postRate uses just those.
  const postable =
    at(totals, "finish:solo") + at(totals, "finish:daily");

  return {
    window: { picks, starts, finishes },
    funnel,
    completionRate: rate(finishes, starts),
    shareRate: rate(at(totals, "share:used"), finishes),
    postRate: rate(at(totals, "score:posted"), postable),
    claimRate: rate(at(totals, "claim:done"), finishes),
    leaderboardViews: at(totals, "pick:leaderboard"),
  };
}

/** One row of the per-player table. */
export interface AdminPlayerRow {
  playerId: string;
  displayName: string;
  handle: string | null;
  avatar: string;
  /** Whether the profile has a recovery passphrase set. */
  claimed: boolean;
  /** Number of recorded results linked to this player. */
  plays: number;
  /** Best correctCount the player has posted. */
  topScore: number;
  /** Fastest finish time in ms (null when the player has no recorded plays). */
  bestTimeMs: number | null;
  /** Mean accuracy (0..1) over their recorded plays. */
  avgAccuracy: number;
  /** Badges earned. */
  badges: number;
  /** ISO timestamps. */
  createdAt: string;
  lastSeenAt: string;
  /** ISO timestamp of the most recent recorded play, or null. */
  lastPlayedAt: string | null;
}

export interface AdminStatsView {
  summary: AdminSummary;
  players: AdminPlayerRow[];
  /** Raw usage-counter totals over the trailing USAGE_WINDOW_DAYS window, kind →
   *  count. The dashboard runs these through usageMetrics() client-side so the
   *  funnel maths stays pure and testable. Empty when the counters table can't be
   *  read (see the defensive query in admin-stats.ts). */
  usage: UsageTotals;
}

/** Raw per-player aggregate, as the DB hands it back (epoch-ms timestamps so the
 *  projection is driver-agnostic and free of Date wrangling). */
export interface RawPlayerAgg {
  playerId: string;
  displayName: string;
  handle: string | null;
  avatar: string;
  claimed: boolean;
  createdAtMs: number;
  lastSeenAtMs: number;
  plays: number;
  topScore: number | null;
  bestTimeMs: number | null;
  avgAccuracy: number | null;
  lastPlayedMs: number | null;
}

const iso = (ms: number): string => new Date(ms).toISOString();

/** Merge per-player aggregates with badge counts and sort the table:
 *  most plays first, then highest top score, then most recently seen. */
export function projectAdminPlayers(
  rows: RawPlayerAgg[],
  badgeCounts: Map<string, number>,
): AdminPlayerRow[] {
  return rows
    .map((r) => ({
      playerId: r.playerId,
      displayName: r.displayName,
      handle: r.handle,
      avatar: r.avatar,
      claimed: r.claimed,
      plays: r.plays,
      topScore: r.topScore ?? 0,
      // min(totalMs) is null when the player has no linked results.
      bestTimeMs: r.plays > 0 ? r.bestTimeMs : null,
      avgAccuracy: r.avgAccuracy ?? 0,
      badges: badgeCounts.get(r.playerId) ?? 0,
      createdAt: iso(r.createdAtMs),
      lastSeenAt: iso(r.lastSeenAtMs),
      lastPlayedAt: r.lastPlayedMs != null ? iso(r.lastPlayedMs) : null,
    }))
    .sort(
      (a, b) =>
        b.plays - a.plays ||
        b.topScore - a.topScore ||
        Date.parse(b.lastSeenAt) - Date.parse(a.lastSeenAt),
    );
}
